import { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Routes } from "react-router-dom";
import AssessmentSection from "@/components/AssessmentSection";
import CourseCatalogSection from "@/components/CourseCatalogSection";
import CurriculumSection from "@/components/CurriculumSection";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import SortingHatSection from "@/components/SortingHatSection";
import StudentWallSection from "@/components/StudentWallSection";
import StudentProfilePage from "@/components/StudentProfilePage";
import StudentsPage from "@/components/StudentsPage";
import StructureSection from "@/components/StructureSection";
import TerminalSection from "@/components/TerminalSection";
import { useSession } from "@/contexts/SessionContext";
import translations from "@/i18n";
import { getInitialLang, persistLang, syncDocumentLang } from "@/i18n/locale";
import { getFoundationsRequiredModules } from "../shared/course-catalog";
import type { Lang } from "@/types";

const BOOT_LOGS = [
  "> boot clawford://foundations",
  "> scan skill manifest",
  "> hydrate first-party curriculum",
  "> reserve exam seat",
  "> reserve academy candidate profile",
];

const MAX_TERMINAL_LOGS = 200;
const REQUIRED_FOUNDATIONS_MODULES = getFoundationsRequiredModules();

function appendLogs(prev: string[], ...lines: string[]): string[] {
  const next = [...prev, ...lines];
  return next.length > MAX_TERMINAL_LOGS ? next.slice(-MAX_TERMINAL_LOGS) : next;
}

interface MainSiteProps {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

function MainSite({ lang, setLang }: MainSiteProps) {
  const {
    transcript,
    isLoading,
    error,
    connect,
    studyModule,
    takeExam,
    updateDisplayName,
  } = useSession();

  const [terminalLogs, setTerminalLogs] = useState<string[]>(BOOT_LOGS);

  const t = translations[lang];

  const isConnected = !!transcript;
  const completedModules = useMemo(
    () => transcript?.foundationsStatus.completedModules.map((item) => item.toUpperCase()) ?? [],
    [transcript],
  );
  const examPassed = transcript?.foundationsStatus.status === "completed";
  const allModulesCompleted = REQUIRED_FOUNDATIONS_MODULES.every((item) => completedModules.includes(item));

  const handleConnect = useCallback(
    async (username: string, password: string, displayName?: string) => {
      setTerminalLogs((prev) => appendLogs(prev, `> authenticating: ${username}...`));
      try {
        await connect(username, password, displayName);
        setTerminalLogs((prev) =>
          appendLogs(prev,
            "> identity verified",
            `> user: ${username}`,
            "> foundations enrolled",
            "> course graph ready",
          ),
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : "connection failed";
        setTerminalLogs((prev) => appendLogs(prev, `> ERROR: ${msg}`));
      }
    },
    [connect],
  );

  const handleStudy = useCallback(
    async (moduleId: string) => {
      if (!isConnected || completedModules.includes(moduleId)) return;
      try {
        await studyModule(moduleId);
        setTerminalLogs((prev) =>
          appendLogs(prev,
            `> module complete: ${moduleId}`,
            "> evidence captured: notes + checklist",
          ),
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : "module study failed";
        setTerminalLogs((prev) => appendLogs(prev, `> ERROR: ${msg}`));
      }
    },
    [isConnected, completedModules, studyModule],
  );

  const handleExam = useCallback(async () => {
    if (!isConnected) return;
    try {
      const updatedTranscript = await takeExam();
      const updatedExamPassed = updatedTranscript?.foundationsStatus.status === "completed";
      setTerminalLogs((prev) =>
        appendLogs(prev,
          "> exam start: scenario-based assessment",
          "> assessment submitted",
          examPassed && updatedExamPassed
            ? "> retake recorded: best score leaderboard recalculated"
            : "> graduation granted: clawford foundations",
        ),
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "exam submission failed";
      setTerminalLogs((prev) => appendLogs(prev, `> ERROR: ${msg}`));
    }
  }, [examPassed, isConnected, takeExam]);

  return (
    <div className="app-shell">
      <div className="backdrop-orb backdrop-orb-a" aria-hidden="true" />
      <div className="backdrop-orb backdrop-orb-b" aria-hidden="true" />

      <a href="#main-content" className="skip-link">
        {t.common.skipToContent}
      </a>

      <Header lang={lang} setLang={setLang} t={t} />

      <main id="main-content">
        <Hero t={t} />
        <StructureSection lang={lang} t={t} />

        <TerminalSection
          t={t}
          isConnected={isConnected}
          isLoading={isLoading}
          error={error}
          terminalLogs={terminalLogs}
          examPassed={examPassed}
          allModulesCompleted={allModulesCompleted}
          requiredModuleCount={REQUIRED_FOUNDATIONS_MODULES.length}
          onConnect={handleConnect}
          onExam={handleExam}
        />

        <CurriculumSection
          lang={lang}
          t={t}
          completedModules={completedModules}
          isConnected={isConnected}
          onStudy={handleStudy}
        />

        <SortingHatSection
          lang={lang}
          t={t}
          uid={transcript?.uid ?? null}
          displayName={transcript?.displayName ?? ""}
          house={transcript?.house ?? null}
          houseVerdict={transcript?.houseVerdict ?? null}
          linkedIds={transcript?.linkedIds ?? []}
          onUpdateDisplayName={updateDisplayName}
        />

        <StudentWallSection lang={lang} t={t} />

        <CourseCatalogSection lang={lang} t={t} />

        <AssessmentSection lang={lang} t={t} />
      </main>

      <Footer t={t} />
    </div>
  );
}

function App() {
  const [lang, setLang] = useState<Lang>(getInitialLang);

  const handleSetLang = useCallback((next: Lang) => {
    setLang(next);
    persistLang(next);
    syncDocumentLang(next);
  }, []);

  useEffect(() => {
    syncDocumentLang(lang);
  }, [lang]);

  return (
    <Routes>
      <Route path="/students/:uid" element={<StudentProfilePage lang={lang} setLang={handleSetLang} />} />
      <Route path="/students" element={<StudentsPage lang={lang} setLang={handleSetLang} />} />
      <Route path="*" element={<MainSite lang={lang} setLang={handleSetLang} />} />
    </Routes>
  );
}

export default App;
